import { BRAND_COLORS } from "@/Utils/brandColors";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useCustomNavigation } from "@/hooks/useCustomNavigation";
import { AnimatedFigureIcon } from "@/Components/AvatarLifeCycle";
import { ScanUploader, UploadEvents } from "@/services/ScanUploader";
import {
  getAvatarSharingData,
  hasActiveSharingSession,
} from "@/Utils/avatarSharingStorage";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import DiscardModal from "@/Components/Modal/DiscardModal";
import RedirectProgress from "@/Components/RedirectProgress";
import { watchOrphanedScanState } from "@/Utils/scanRecovery";

import "./avatarGeneration.scss";

// Small green check used in the success status rows (matches SubmitScan v2).
const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path
      d="M2.5 6.2 L5 8.6 L9.5 3.6"
      stroke={BRAND_COLORS.success}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AvatarGeneration = () => {
  const { navigateBack, navigateTo } = useCustomNavigation();

  const location = useLocation();
  const { date = "", name = "" } = location.state || {};
  const [loadPercentage, setLoadPercentage] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [displayRedirectCountdown, setDisplayRedirectCountdown] =
    useState(false);
  const handleClose = () => {
    setOpen(false);
  };

  const scanUploader = ScanUploader.getInstance();
  // Snapshot at mount so a mid-flow clear (ScanModel.create clears sharing data
  // on submit success) can't wipe the redirect target before we use it — mirrors
  // SubmitScan.
  const sharingData = useMemo(() => getAvatarSharingData(), []);
  const isSharing = hasActiveSharingSession();
  const orgName = sharingData.orgName || "";

  // Figure turns green (submitted) on SUCCESS or once progress reaches 100%.
  const submitted = !loading || loadPercentage === 100;

  useEffect(() => {
    // This screen is reached from the avatars-list "Submitting Scans ..."
    // card — or restored directly by the browser on reopen. Either way it
    // only ever hears from the in-memory uploader, which after a
    // reload/browser-close is a fresh singleton that never emits. Reconcile
    // the leftover localStorage state: if the submission actually completed
    // server-side, show the success state; if the scan is dead, there is
    // nothing to watch — leave to the avatars list (the cleared keys mean
    // the phantom card is gone). A live submission (this tab's uploader owns
    // the scanID) returns null and is untouched.
    // Watch rather than check once — see the note in SubmitScan: the heartbeat
    // only becomes decisive a few seconds after the owning page dies, so a
    // mount-time peek misses a quick reopen entirely.
    const stopScanWatch = watchOrphanedScanState("AvatarGeneration", (outcome) => {
      if (outcome === "submitted") {
        setLoadPercentage(100);
        setLoading(false);
      } else {
        // Leaving during an active share must go through /share-cancelled, not
        // straight back: that screen owns the widget-session reset (progress
        // "IDLE") + the ttf-cancelled redirect. Exiting past it strands the
        // store widget on "follow instructions" forever — the exact leak
        // v2.4.9/2.4.10 closed. Same gate the SUCCESS handler below uses, and
        // the same destination DiscardModal reaches for a discarded scan.
        // (Until BUG-0012 this was false after a browser close — the missing
        // sessionStorage deeplink flag made hasActiveSharingSession()
        // self-clear — so only the reload case took this path. That guard is
        // gone: a share now lives until its scan is approved, the 2-hour
        // window ends, or the shopper cancels, so a page the OS discarded and
        // the browser rebuilt reaches this branch too, and the store widget
        // gets its reset instead of being left waiting.)
        if (isSharing && sharingData.redirectUrl) {
          navigateTo("/share-cancelled", { state: { discarded: true } });
        } else {
          navigateBack();
        }
      }
    });

    const cleanupProgress = scanUploader.on(UploadEvents.PROGRESS, (progress) => {
      setLoadPercentage(progress);
    });
    const cleanupSuccess = scanUploader.on(UploadEvents.SUCCESS, () => {
      setLoading(false);
      // Closed loop: once the shared scan finishes uploading, return the user to
      // the store (same gate/behaviour as SubmitScan). Only when there's an
      // active session AND a URL, so a stale redirectUrl can't hijack a
      // non-shared scan.
      if (isSharing && sharingData.redirectUrl) {
        setDisplayRedirectCountdown(true);
      }
    });
    return () => {
      stopScanWatch();
      cleanupProgress?.();
      cleanupSuccess?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="avatar-generation">
      <DiscardModal open={open} handleClose={handleClose} />

      {/* No passive exit during a share (closed loop): the only ways off are
          Cancel → discard or success → redirect. Outside a share this is a
          detail view opened from the list, so keep the back chevron. */}
      {!isSharing && (
        <button
          type="button"
          className="avatar-generation__back"
          onClick={navigateBack}
          aria-label="Back"
        >
          <ChevronLeftIcon />
        </button>
      )}

      <div className="avatar-generation__main">
        <div className="avatar-generation__figure">
          <AnimatedFigureIcon
            color={BRAND_COLORS.primaryTint}
            fillPercent={loadPercentage}
            toColor={BRAND_COLORS.primary0670}
            submitted={submitted}
          />
        </div>

        <h1 className="avatar-generation__title">
          {loading ? "Submitting scan" : "Scans submitted!"}
        </h1>
        <p className="avatar-generation__subtitle">
          {loading
            ? "This will only take a moment. Once submitted, your avatar will start processing."
            : "Your avatar is now being processed. Keep track of its progress from your avatars list."}
        </p>

        {loading && isSharing && orgName && (
          <p className="avatar-generation__share-note">
            It will be shared with <b>{orgName}</b> once processed.
          </p>
        )}

        {!loading && (
          <div className="avatar-generation__status">
            <div className="avatar-generation__status-row">
              <span className="avatar-generation__status-icon">
                <CheckIcon />
              </span>
              <span className="avatar-generation__status-label">
                Scan submitted
              </span>
            </div>
            <div className="avatar-generation__status-divider" />
            <div className="avatar-generation__status-row">
              <span className="avatar-generation__status-icon">
                <CheckIcon />
              </span>
              <span className="avatar-generation__status-label">
                Avatar queued for processing
              </span>
            </div>
            {isSharing && orgName && (
              <>
                <div className="avatar-generation__status-divider" />
                <div className="avatar-generation__status-row">
                  <span className="avatar-generation__status-icon">
                    <CheckIcon />
                  </span>
                  <span className="avatar-generation__status-label">
                    Will be shared with <b>{orgName}</b>
                  </span>
                </div>
              </>
            )}
          </div>
        )}

        {(name || date) && (
          <div className="avatar-generation__meta">
            {name}
            {name && date ? " • " : ""}
            {date}
          </div>
        )}
      </div>

      <div className="avatar-generation__footer">
        {loading ? (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="avatar-generation__cancel"
          >
            Cancel
          </button>
        ) : displayRedirectCountdown ? (
          <RedirectProgress variant="minimal" durationSeconds={4} />
        ) : null}
      </div>
    </div>
  );
};

export default AvatarGeneration;
